import { useOkapiKy } from '@folio/stripes-core';
import { useQuery } from 'react-query';

const TRANSLATION_KEYS_API = 'translation-keys';
const DEFAULT_LIMIT = 50;

/**
 * Fetches translation keys from GET /translation-keys
 *
 * @param {object} params
 * @param {string} params.categoryName  - required: libTranslations | stripesApps | permsNames
 * @param {string} params.localeValue   - e.g. 'ar', 'en'
 * @param {string} params.appName       - e.g. 'ui-users'
 * @param {string} params.tableName     - e.g. 'patronGroups'
 * @param {string[]} params.status      - e.g. ['translated', 'notTranslated']
 * @param {string} params.query         - free-text search on value/sourceValue
 * @param {number} params.offset        - pagination offset
 * @param {number} params.limit         - page size (default 50)
 * @param {boolean} params.enabled      - whether to run the query
 */
export function useTranslationKeys({
  categoryName,
  localeValue,
  appName,
  tableName,
  status = [],
  query = '',
  offset = 0,
  limit = DEFAULT_LIMIT,
  enabled = true,
} = {}) {
  const ky = useOkapiKy();

  const cql = buildCQL({ categoryName, localeValue, appName, tableName, status, query });

  return useQuery(
    [TRANSLATION_KEYS_API, cql, offset, limit],
    () =>
      ky
        .get(TRANSLATION_KEYS_API, {
          searchParams: { query: cql, offset, limit },
        })
        .json(),
    {
      enabled: enabled && !!categoryName,
      keepPreviousData: true,
    },
  );
}

/**
 * Builds a CQL query string from filter params.
 */
export function buildCQL({
  categoryName,
  localeValue,
  appName,
  tableName,
  status = [],
  query = '',
} = {}) {
  const parts = [];

  if (categoryName) parts.push(`categoryName=="${categoryName}"`);
  if (localeValue)  parts.push(`localeValue=="${localeValue}"`);
  if (appName && appName !== 'allApps') {
    if (Array.isArray(appName) && appName.length) {
      const orParts = appName.map((a) => `appName=="${a}"`).join(' or ');
      parts.push(appName.length > 1 ? `(${orParts})` : orParts);
    } else if (typeof appName === 'string') {
      parts.push(`appName=="${appName}"`);
    }
  }
  if (tableName && tableName !== 'allTables') {
    if (Array.isArray(tableName) && tableName.length) {
      const orParts = tableName.map((t) => `tableName=="${t}"`).join(' or ');
      parts.push(tableName.length > 1 ? `(${orParts})` : orParts);
    } else if (typeof tableName === 'string') {
      parts.push(`tableName=="${tableName}"`);
    }
  }
  if (status.length) {
    const statusCql = status.map((s) => `status=="${s}"`).join(' or ');
    parts.push(`(${statusCql})`);
  }

  if (query.trim()) {
    parts.push(`(value="${query.trim()}*" or sourceValue="${query.trim()}*")`);
  }

  const where = parts.length ? parts.join(' and ') : 'cql.allRecords=1';
  return `${where} sortby translationKey`;
}
